import { FC, MutableRefObject, useEffect } from 'react';
import { Toast, ToastMessage } from 'primereact/toast';
import { CircleAlert, CircleCheck, X } from 'lucide-react';

const TOAST_LIFE_MS = 4000;
const ERROR_TOAST_LIFE_MS = 8000;

const successTone = { card: 'tw-border-green-800 tw-bg-green-700', Icon: CircleCheck };
const errorTone = { card: 'tw-border-red-700 tw-bg-red-600', Icon: CircleAlert };

const toneFor = (severity: ToastMessage['severity']) => (severity === 'error' ? errorTone : successTone);

const lifeFor = (severity: ToastMessage['severity']) => (severity === 'error' ? ERROR_TOAST_LIFE_MS : TOAST_LIFE_MS);

interface CardProps {
  message: ToastMessage;
  toastRef: MutableRefObject<Toast | null>;
}

const ExitSlipToastCard: FC<CardProps> = ({ message, toastRef }) => {
  const { card, Icon } = toneFor(message.severity);

  useEffect(() => {
    const timer = setTimeout(() => toastRef.current?.remove(message), lifeFor(message.severity));
    return () => clearTimeout(timer);
  }, [message, toastRef]);

  return (
    <div
      className={`tw-flex tw-w-full tw-items-start tw-gap-3 tw-rounded-2xl tw-border-2 tw-border-b-8 tw-border-solid tw-p-4 tw-font-atkinson tw-text-neutral-0 ${card}`}
    >
      <Icon size={24} className="tw-shrink-0" aria-hidden />
      <div className="tw-flex tw-min-w-0 tw-grow tw-flex-col tw-gap-2">
        <span className="tw-text-base tw-font-bold tw-leading-5 [text-wrap:pretty]">{message.summary}</span>
        {!!message.detail && <span className="tw-text-sm tw-leading-5 [text-wrap:pretty]">{message.detail}</span>}
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => toastRef.current?.remove(message)}
        className="tw-flex tw-size-6 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-none tw-bg-transparent tw-p-0 tw-text-neutral-0"
      >
        <X size={16} strokeWidth={2.5} aria-hidden />
      </button>
    </div>
  );
};

interface Props {
  toastRef: MutableRefObject<Toast | null>;
}

const ExitSlipModalToast: FC<Props> = ({ toastRef }) => (
  <Toast
    ref={toastRef}
    className="tw-opacity-100"
    content={({ message }) => <ExitSlipToastCard message={message as ToastMessage} toastRef={toastRef} />}
  />
);

export default ExitSlipModalToast;
